if (!customElements.get('zentron-fitment')) {
  customElements.define('zentron-fitment', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const config = JSON.parse(this.querySelector('[data-zf-config]').textContent);
      this.strings = config.strings;
      this.mode = config.mode;
      this.querySelectorAll('[data-zf-other], [data-zf-mega-link]').forEach(link => link.addEventListener('click', event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(link.href);
      }));
      this.steps = [...this.querySelectorAll('[data-zf-step]')];
      this.current = 0;
      this.furthest = 0;
      this.querySelector('[data-zf-next]').addEventListener('click', () => {
        if (this.validateStep(this.current)) this.showStep(this.current + 1);
      });
      this.querySelector('[data-zf-back]').addEventListener('click', () => this.showStep(this.current - 1));
      this.querySelectorAll('[data-zf-go]').forEach(button => button.addEventListener('click', () => {
        const step = Number(button.dataset.zfGo);
        if (step > this.current) {
          for (let prior = 0; prior < step; prior += 1) {
            if (!this.validateStep(prior, false)) {
              this.showStep(prior);
              this.validateStep(prior);
              return;
            }
          }
        }
        this.showStep(step);
      }));
      this.addEventListener('change', () => { this.syncFields(); this.clearSubmissionFeedback(); });
      this.addEventListener('input', event => {
        this.clearError(event.target);
        this.clearSubmissionFeedback();
      });
      this.contactForm = this.querySelector('form[data-zf-form]');
      this.serverStatus = this.querySelector('[data-zf-server-state]').dataset.status;
      if (this.serverStatus === 'success') {
        this.classList.add('is-submitted');
        this.clearPendingRequest();
        this.querySelector('[data-zf-success]').focus();
        return;
      }
      this.contactForm.addEventListener('submit', event => this.submitRequest(event), true);
      window.addEventListener('pageshow', () => this.resetSubmitting());
      this.prefill();
      const restored = this.restorePendingRequest();
      this.syncFields();
      this.showStep(restored || this.serverStatus === 'error' ? 3 : (this.fromKnownDevice ? 1 : 0), false);
      if (this.serverStatus === 'error') this.querySelector('[data-zf-server-error]').focus();
    }

    field(name) {
      const aliases = { email: 'contact[email]', name: 'contact[name]' };
      return this.querySelector('[name="' + (aliases[name] || name) + '"]');
    }

    value(name) {
      const first = this.field(name);
      if (!first) return '';
      if (first.type === 'radio') return this.querySelector('[name="' + name + '"]:checked:not(:disabled)')?.value || '';
      return first.disabled ? '' : first.value;
    }

    syncFields() {
      const selected = name => this.querySelector('[name="' + name + '"]:checked')?.value || '';
      const cq25r = this.mode === 'mega' && this.value('device') === 'cq25r';
      const helmetOption = this.querySelector('[name="mount_route"][value="helmet"]');
      if (helmetOption && !cq25r) helmetOption.checked = false;
      const route = selected('mount_route');
      const kit = selected('kit_choice');
      const cRoute = this.mode === 'adapter' ? selected('c_mount_route') : '';
      const cModular = cRoute === 'modular';
      const cRail = cRoute === 'rail';
      const cSeries = ['c35', 'zen-c50-thermal-clip-on', 'zen-c50r-640x512-50mm-thermal-clip-on'].includes(this.value('device'));
      const conditions = {
        cq25r,
        c_modular: cModular,
        c_rail: cRail,
        c_help: cRoute === 'not_sure',
        measured: cModular && selected('measurement_status') === 'measured',
        scope_lookup: cModular && selected('measurement_status') === 'lookup',
        ac02: route === 'ac02',
        ac03: route === 'ac03',
        helmet: cq25r && route === 'helmet',
        rail_mount: ['ac02', 'ac03', 'not_sure'].includes(route),
        rail_dual_mode: cq25r && route === 'ac02' && ['ac02_15_riser', 'ac02_17_riser', 'ac02_15_dovetail'].includes(kit),
        unknown_route: route === 'not_sure',
        smartclip_ring: cModular && cSeries && selected('adapter_system') === 'smartclip',
        rusan_ring: cModular && cSeries && selected('adapter_system') === 'rusan'
      };
      const groups = [...this.querySelectorAll('[data-zf-when]')];
      groups.forEach(group => { group.hidden = !Boolean(conditions[group.dataset.zfWhen]); });
      const fields = new Set(groups.flatMap(group => [...group.querySelectorAll('input, select, textarea')]));
      fields.forEach(field => {
        field.disabled = Boolean(field.closest('[data-zf-when][hidden]'));
        if (field.disabled) this.clearError(field);
      });
      this.updateHeight();
      this.updateAssemblyPreview();
      const otherGuide = this.querySelector('[data-zf-other]');
      if (otherGuide) {
        const url = new URL(otherGuide.href);
        url.searchParams.delete('fitment_source');
        otherGuide.href = url.toString();
      }
    }

    heightLabel() {
      if (this.mode === 'adapter') {
        if (this.value('c_mount_route') !== 'rail') return '';
        const riser = this.value('c_riser');
        if (riser === 'c15') return this.strings.axis_height_15;
        if (riser === 'c17') return this.strings.axis_height_17;
        return this.strings.axis_select_height;
      }
      if (this.mode !== 'mega') return '';
      const route = this.value('mount_route');
      if (route === 'helmet') return this.strings.axis_height_helmet;
      if (route === 'ac03') {
        const heights = {ac03_15_riser: 'axis_height_ac03_15', ac03_17_riser: 'axis_height_ac03_17', ac03_dovetail: 'axis_height_ac03_dv'};
        return this.strings[heights[this.value('ac03_part')]] || this.strings.axis_height_ac03;
      }
      if (route !== 'ac02') return this.strings.axis_select_height;
      const kit = this.value('kit_choice');
      if (kit === 'ac02_cq_extension') return this.strings.axis_height_cq;
      if (kit === 'ac02_17_riser') return this.strings.axis_height_17;
      if (['ac02_15_riser', 'ac02_15_dovetail'].includes(kit)) return this.strings.axis_height_15;
      return this.strings.axis_select_height;
    }

    updateHeight() {
      this.querySelectorAll('[data-zf-height]').forEach(node => { node.textContent = this.heightLabel(); });
      this.querySelectorAll('[data-zf-c-route-height]').forEach(node => {
        const riser = this.value('c_riser');
        node.textContent = riser === 'c15' ? node.getAttribute('data-height-15') : riser === 'c17' ? node.getAttribute('data-height-17') : node.dataset.heightDefault;
      });
    }

    assemblyKey() {
      if (this.mode !== 'mega' || this.value('device') !== 'cq25r') return '';
      if (this.value('mount_route') === 'helmet') return 'helmet_dv';
      if (this.value('mount_route') === 'ac03') {
        const part = this.value('ac03_part');
        return ['ac03_15_riser', 'ac03_17_riser', 'ac03_dovetail'].includes(part) ? part : 'ac03';
      }
      if (this.value('mount_route') !== 'ac02') return '';
      const kit = this.value('kit_choice');
      return ['ac02_15_riser', 'ac02_17_riser', 'ac02_15_dovetail', 'ac02_cq_extension'].includes(kit) ? kit : '';
    }

    updateAssemblyPreview() {
      const wrapper = this.querySelector('[data-zf-assembly-preview]');
      if (!wrapper) return;
      const key = this.assemblyKey();
      wrapper.hidden = !key;
      wrapper.querySelectorAll('[data-zf-setup-preview]').forEach(figure => {
        figure.hidden = figure.dataset.zfSetupPreview !== key;
      });
    }

    renderProductReview() {
      const target = this.querySelector('[data-zf-review-products]');
      target.replaceChildren();
      const products = new Map();
      const add = (field, title, role, key, imageOverride) => {
        if (!field || field.disabled || !field.checked) return;
        const art = imageOverride || field.closest('label')?.querySelector('[data-zf-review-art] img') || field.closest('label')?.querySelector('img');
        if (!art) return;
        if (products.has(key)) {
          const item = products.get(key);
          if (!item.roles.includes(role)) item.roles.push(role);
          return;
        }
        products.set(key, { art, title, roles: [role] });
      };
      const checked = name => this.querySelector('[name="' + name + '"]:checked:not(:disabled)');
      const device = checked('device');
      add(device, device?.dataset.zfDisplay, this.strings.review_device, 'device');
      if (this.mode === 'mega') {
        const route = checked('mount_route');
        const owned = checked('existing_mount');
        const cq25r = this.value('device') === 'cq25r';
        const roleFor = key => {
          if (!cq25r) return this.strings.review_requested;
          if (key === 'ac03' && owned?.value === 'ac03') return this.strings.review_owned;
          return ['ac03', 'ac02_cq_extension'].includes(key) ? this.strings.cq25r_optional_badge : this.strings.cq25r_included_badge;
        };
        if (route?.value === 'helmet') {
          add(route, 'ZT-AC02DV', roleFor('helmet'), 'helmet_dv');
        } else {
          if (route && ['ac02', 'ac03'].includes(route.value)) add(route, route.value === 'ac02' ? 'ZT-AC02' : 'ZT-AC03', roleFor(route.value), route.value);
          const kit = checked('kit_choice');
          const skus = {ac02_15_riser: 'ZT-AC0215CQ', ac02_17_riser: 'ZT-AC0217CQ', ac02_15_dovetail: 'ZT-AC02DV', ac02_cq_extension: 'ZT-AC02CQ'};
          if (kit && skus[kit.value]) add(kit, skus[kit.value], roleFor(kit.value), kit.value);
          if (route?.value === 'ac03') {
            const part = checked('ac03_part');
            const partSkus = {ac03_15_riser: 'ZT-AC0215CQ', ac03_17_riser: 'ZT-AC0217CQ', ac03_dovetail: 'ZT-AC02DV'};
            if (part && partSkus[part.value]) add(part, partSkus[part.value], roleFor(part.value), part.value);
          }
          if (owned && ['ac02', 'ac03'].includes(owned.value)) add(owned, owned.value === 'ac02' ? 'ZT-AC02' : 'ZT-AC03', this.strings.review_owned, owned.value);
        }
      } else {
        const route = checked('c_mount_route');
        if (route?.value === 'rail') {
          add(route, 'ZT-AC02', this.strings.review_requested, 'ac02');
          const riser = checked('c_riser');
          const skus = {c15: 'ZT-AC0215C', c17: 'ZT-AC0217C'};
          if (riser && skus[riser.value]) add(riser, skus[riser.value], this.strings.review_requested, riser.value);
        } else if (route?.value === 'modular') {
          const system = checked('adapter_system');
          add(system, system?.dataset.zfDisplay, this.strings.review_preference, system?.value);
          const cSeries = ['c35', 'zen-c50-thermal-clip-on', 'zen-c50r-640x512-50mm-thermal-clip-on'].includes(this.value('device'));
          if (cSeries && system && ['smartclip', 'rusan'].includes(system.value)) {
            const ring = this.querySelector('[data-zf-ring="' + system.value + '"] img');
            if (ring) add(system, this.strings['ring_' + system.value], this.strings.review_ring, system.value + '_ring', ring);
          }
        }
      }
      for (const product of products.values()) {
        const figure = document.createElement('figure');
        figure.className = 'zf-review-product';
        const art = document.createElement('div');
        art.className = 'zf-review-product-art';
        const img = product.art.cloneNode(true);
        img.loading = 'lazy';
        img.alt = product.title;
        art.append(img);
        const caption = document.createElement('figcaption');
        caption.className = 'zf-review-product-caption';
        const title = document.createElement('strong');
        title.textContent = product.title;
        const role = document.createElement('span');
        role.className = 'zf-review-product-role';
        role.textContent = product.roles.join(' · ');
        caption.append(title, role);
        figure.append(art, caption);
        target.append(figure);
      }
      target.closest('section').hidden = products.size === 0;
    }

    showStep(index, focus = true) {
      this.current = Math.max(0, Math.min(3, index));
      this.furthest = Math.max(this.current, this.furthest);
      this.steps.forEach((step, i) => { step.hidden = i !== this.current; });
      this.querySelectorAll('[data-zf-go]').forEach((button, i) => {
        button.disabled = i > this.furthest;
        button.classList.toggle('is-current', i === this.current);
        button.classList.toggle('is-complete', i < this.current);
        if (i === this.current) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      this.querySelector('[data-zf-back]').hidden = this.current === 0;
      this.querySelector('[data-zf-next]').hidden = this.current === 3;
      this.querySelector('[data-zf-submit]').hidden = this.current !== 3;
      if (this.current === 3) this.renderReview();
      this.clearSubmissionFeedback();
      if (focus) {
        const title = this.steps[this.current].querySelector('h2');
        title.focus({ preventScroll: true });
        title.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
    }

    clearError(field) {
      const wrapper = field.closest?.('[data-zf-field]');
      if (!wrapper) return;
      wrapper.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
      const error = wrapper.querySelector('[data-zf-error]');
      if (error) { error.hidden = true; error.textContent = ''; }
    }

    setError(field, message) {
      const wrapper = field.closest('[data-zf-field]');
      field.setAttribute('aria-invalid', 'true');
      const error = wrapper?.querySelector('[data-zf-error]');
      if (error) { error.textContent = message; error.hidden = false; }
    }

    validHttps(value) {
      try { const url = new URL(value); return url.protocol === 'https:' && Boolean(url.hostname) && !url.username && !url.password; }
      catch { return false; }
    }

    validateStep(index, focus = true) {
      let firstInvalid = null;
      const radioNames = new Set();
      for (const field of this.steps[index].querySelectorAll('input, select, textarea')) {
        if (field.disabled) continue;
        if (field.type === 'radio' && radioNames.has(field.name)) continue;
        this.clearError(field);
        let error = '';
        if (field.type === 'radio') {
          radioNames.add(field.name);
          if (field.required && !this.querySelector('[name="' + field.name + '"]:checked:not(:disabled)')) error = field.name === 'device' ? this.strings.error_device : this.strings.error_choice;
        } else if (field.required && !field.value.trim()) error = this.strings.error_required;
        else if (field.name === 'reference_scope_model' && field.value && !this.querySelector('zentron-scope-lookup')?.validSelection()) error = this.querySelector('zentron-scope-lookup')?.requiresMeasurement() ? this.strings.lookup_conflict : this.strings.error_choice;
        else if (field.type === 'email' && field.value && !field.validity.valid) error = this.strings.error_email;
        else if (field.name === 'diameter' && field.value && (!Number.isFinite(Number(field.value)) || Number(field.value) <= 0)) error = this.strings.error_diameter;
        else if (field.type === 'url' && field.value && !this.validHttps(field.value.trim())) error = this.strings.error_url;
        else if (field.name === 'photo_links' && field.value.trim() && !field.value.trim().split(/\r?\n/).every(line => !line.trim() || this.validHttps(line.trim()))) error = this.strings.error_url;
        if (error) { this.setError(field, error); firstInvalid ||= field; }
      }
      if (firstInvalid && focus) firstInvalid.focus();
      return !firstInvalid;
    }

    renderReview() {
      const target = this.querySelector('[data-zf-review]');
      target.replaceChildren();
      this.renderProductReview();
      const titles = [this.strings.step_device, this.strings.step_setup, this.strings.step_details];
      this.steps.slice(0, 3).forEach((step, index) => {
        const card = document.createElement('section');
        card.className = 'zf-review-card';
        const header = document.createElement('header');
        const heading = document.createElement('h3');
        heading.textContent = titles[index];
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.textContent = this.strings.edit;
        edit.setAttribute('aria-label', this.strings.edit + ' ' + titles[index]);
        edit.addEventListener('click', () => this.showStep(index));
        header.append(heading, edit);
        const list = document.createElement('dl');
        const seen = new Set();
        for (const field of step.querySelectorAll('input, select, textarea')) {
          if (!field.dataset.zfLabel || field.disabled || seen.has(field.name) || !field.value.trim()) continue;
          if (this.value('measurement_status') === 'lookup' && ['optic_brand', 'optic_model'].includes(field.name)) continue;
          if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
          seen.add(field.name);
          const term = document.createElement('dt');
          term.textContent = field.dataset.zfLabel || field.name;
          const value = document.createElement('dd');
          if (field.tagName === 'SELECT') value.textContent = field.selectedOptions[0].textContent;
          else if (field.type === 'checkbox') value.textContent = field.dataset.zfLabel;
          else value.textContent = field.dataset.zfDisplay || field.value.trim();
          list.append(term, value);
        }
        if (!list.children.length) { const note = document.createElement('dd'); note.textContent = this.strings.empty; list.append(note); }
        const showHeight = (this.mode === 'mega' && this.value('mount_route') !== 'helmet') || (this.mode === 'adapter' && this.value('c_mount_route') === 'rail');
        if (index === 2 && showHeight) {
          const term = document.createElement('dt');
          term.textContent = this.strings.review_height;
          const height = document.createElement('dd');
          height.textContent = this.heightLabel();
          list.append(term, height);
        }
        card.append(header, list);
        target.append(card);
      });
    }

    clearSubmissionFeedback() {
      if (!this.submitting) this.querySelector('[data-zf-submit-status]').textContent = '';
    }

    submitRequest(event) {
      if (this.current < 3) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.validateStep(this.current)) this.showStep(this.current + 1);
        return;
      }
      this.syncFields();
      for (let step = 0; step < 4; step += 1) {
        if (!this.validateStep(step, false)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showStep(step);
          this.validateStep(step);
          return;
        }
      }
      this.renderReview();
      this.prepareSubmission();
      this.savePendingRequest();
      this.submitting = true;
      const button = this.querySelector('[data-zf-submit]');
      button.disabled = true;
      button.textContent = this.strings.sending_request;
      this.contactForm.setAttribute('aria-busy', 'true');
      clearTimeout(this.submissionTimer);
      this.submissionTimer = setTimeout(() => this.resetSubmitting(this.strings.submit_waiting), 30000);
    }

    prepareSubmission() {
      const source = new URL(window.location.pathname, window.location.origin);
      source.searchParams.set('view', this.mode === 'mega' ? 'mega-mount-fitment' : 'clip-on-adapter-fitment');
      this.querySelector('[data-zf-origin]').value = source.href;
      const lines = [this.strings.request_heading, this.querySelector('h1').textContent.trim(), '', this.strings.request_products_heading];
      this.querySelectorAll('[data-zf-review-products] .zf-review-product').forEach(product => {
        const title = product.querySelector('strong').textContent.trim();
        const role = product.querySelector('.zf-review-product-role').textContent.trim();
        lines.push(title + (role ? ' — ' + role : ''));
        const image = product.querySelector('img');
        if (image && (image.currentSrc || image.getAttribute('src'))) {
          const url = new URL(image.currentSrc || image.getAttribute('src'), window.location.origin);
          if (url.protocol === 'https:') lines.push(url.href);
        }
      });
      lines.push('', this.strings.request_details_heading);
      const seen = new Set();
      for (const step of this.steps) {
        for (const field of step.querySelectorAll('input, select, textarea')) {
          if (!field.dataset.zfLabel || field.disabled || seen.has(field.name) || !field.value.trim()) continue;
          if (this.value('measurement_status') === 'lookup' && ['optic_brand', 'optic_model'].includes(field.name)) continue;
          if (['radio', 'checkbox'].includes(field.type) && !field.checked) continue;
          seen.add(field.name);
          const value = field.tagName === 'SELECT' ? field.selectedOptions[0].textContent.trim() : field.dataset.zfDisplay || field.value.trim();
          lines.push(field.dataset.zfLabel + ': ' + value);
        }
      }
      const hasHeight = (this.mode === 'mega' && this.value('mount_route') !== 'helmet') || (this.mode === 'adapter' && this.value('c_mount_route') === 'rail');
      if (hasHeight) lines.push(this.strings.review_height + ': ' + this.heightLabel());
      lines.push('', this.strings.request_source_label + ': ' + source.href);
      this.querySelector('[data-zf-body]').value = lines.join('\n');
    }

    pendingKey() { return 'zentron-fitment-pending:' + this.mode + ':' + window.location.pathname; }

    savePendingRequest() {
      const fields = this.steps.flatMap(step => [...step.querySelectorAll('input, select, textarea')])
        .filter(field => field.dataset.zfLabel && !field.disabled && (!['radio', 'checkbox'].includes(field.type) || field.checked))
        .map(field => ({ name: field.name, value: field.value }));
      const draft = { version: 1, at: Date.now(), mode: this.mode, source: new URL(window.location.href).searchParams.get('fitment_source') || '', setup: new URL(window.location.href).searchParams.get('fitment_setup') || '', fields };
      try { sessionStorage.setItem(this.pendingKey(), JSON.stringify(draft)); } catch {}
    }

    restorePendingRequest() {
      try {
        const draft = JSON.parse(sessionStorage.getItem(this.pendingKey()) || 'null');
        if (!draft) return false;
        if (draft.version !== 1 || draft.mode !== this.mode || !Array.isArray(draft.fields) || !Number.isFinite(draft.at) || Date.now() - draft.at > 1800000 || Date.now() < draft.at) {
          this.clearPendingRequest();
          return false;
        }
        const source = new URL(window.location.href).searchParams.get('fitment_source') || '';
        const setup = new URL(window.location.href).searchParams.get('fitment_setup') || '';
        if (draft.source !== source || (draft.setup || '') !== setup) return false;
        const fields = this.steps.flatMap(step => [...step.querySelectorAll('input, select, textarea')]);
        this.querySelector('zentron-scope-lookup')?.restoreSelection(draft.fields);
        for (const saved of draft.fields) {
          if (typeof saved.name !== 'string' || typeof saved.value !== 'string' || saved.value.length > 4096) continue;
          const options = fields.filter(field => field.dataset.zfLabel && field.name === saved.name);
          for (const field of options) {
            if (['radio', 'checkbox'].includes(field.type)) field.checked = field.value === saved.value;
            else if (field.tagName !== 'SELECT' || [...field.options].some(option => option.value === saved.value)) field.value = saved.value;
          }
        }
        this.querySelector('zentron-scope-lookup')?.syncSelection();
        return true;
      } catch { return false; }
    }

    clearPendingRequest() {
      try { sessionStorage.removeItem(this.pendingKey()); } catch {}
    }

    resetSubmitting(message = '') {
      clearTimeout(this.submissionTimer);
      this.submitting = false;
      this.contactForm.removeAttribute('aria-busy');
      const button = this.querySelector('[data-zf-submit]');
      button.disabled = false;
      button.textContent = this.strings.send_request;
      this.querySelector('[data-zf-submit-status]').textContent = message;
    }

    prefill() {
      const source = new URL(window.location.href).searchParams.get('fitment_source');
      if (this.mode === 'adapter' && source === 'cq25r') {
        const megaLink = this.querySelector('[data-zf-mega-link]');
        if (megaLink) window.location.replace(megaLink.href);
        return;
      }
      const products = {
        'c35': ['C35', 'c35'],
        'zen-c50-thermal-clip-on': ['C50', 'zen-c50-thermal-clip-on'],
        'zen-c50r-640x512-50mm-thermal-clip-on': ['C50R', 'zen-c50r-640x512-50mm-thermal-clip-on'],
        'cq25r': ['MEGA CQ25R', 'cq25r'],
        'adapter-ring-connector-for-zentron-c-series-m45-0-75': ['Rusan MCR-M45P M45 × 0.75 Connector Ring', ''],
        'rusan-mar-fx-day-scope-clamp-with-3-screws-paired-with-mcr-m45p-connector': ['Rusan MAR-FX clamp', ''],
        'smartclip-ds-modular-adapter-riflescope-side': ['Smartclip DS scope-side adapter', ''],
        'smartclip-dual-smart-m45-connector-ring': ['Smartclip M45 connector ring', ''],
        'zen-c-q-cq-series-quick-release-mounts-zt-ac02': ['ZT-AC02 base', ''],
        'cq25-series-mount-zt-ac0215cq-17cq': ['MEGA height riser', ''],
        'mega-series-dovetail-adapter-zt-ac02dv': ['MEGA dovetail adapter', ''],
        'mega-flip-to-side-mount-zt-ac03': ['MEGA flip-to-side mount', ''],
        'q-series-mount-zt-ac02qd': ['ZT-AC02CQ MEGA lift and rearward-extension adapter', ''],
        'zen-c-and-q-series-mount-zt-ac02151-5-inches': ['C Series height adapter — ZT-AC0215C / ZT-AC0217C', '']
      };
      if (!source || !Object.hasOwn(products, source)) return;
      const [title, model] = products[source];
      const allowedModel = [...this.querySelectorAll('[name="device"]')].find(input => input.value === model);
      if (allowedModel) {
        allowedModel.checked = true;
        this.fromKnownDevice = true;
        this.classList.add('zr-fitment-known');
      }
      const choose = (name, value) => {
        const option = [...this.querySelectorAll('[name="' + name + '"]')].find(input => input.value === value);
        if (option) option.checked = true;
      };
      if (this.mode === 'mega') {
        if (source === 'mega-flip-to-side-mount-zt-ac03') choose('mount_route', 'ac03');
        if (['zen-c-q-cq-series-quick-release-mounts-zt-ac02', 'cq25-series-mount-zt-ac0215cq-17cq', 'q-series-mount-zt-ac02qd'].includes(source)) choose('mount_route', 'ac02');
        if (source === 'q-series-mount-zt-ac02qd') choose('kit_choice', 'ac02_cq_extension');
      } else {
        if (['zen-c-and-q-series-mount-zt-ac02151-5-inches', 'zen-c-q-cq-series-quick-release-mounts-zt-ac02'].includes(source)) choose('c_mount_route', 'rail');
        if (source.startsWith('smartclip-')) {
          choose('c_mount_route', 'modular');
          choose('adapter_system', 'smartclip');
        }
        if (source.startsWith('rusan-') || source === 'adapter-ring-connector-for-zentron-c-series-m45-0-75') {
          choose('c_mount_route', 'modular');
          choose('adapter_system', 'rusan');
        }
      }
      if (this.mode === 'mega' && source === 'cq25r') {
        const setup = new URL(window.location.href).searchParams.get('fitment_setup');
        const setups = {
          ac02_15_riser: ['ac02', 'ac02_15_riser'],
          ac02_17_riser: ['ac02', 'ac02_17_riser'],
          ac02_15_dovetail: ['ac02', 'ac02_15_dovetail'],
          ac02_cq_extension: ['ac02', 'ac02_cq_extension'],
          ac03: ['ac03', ''],
          helmet_dv: ['helmet', '']
        };
        if (setup && Object.hasOwn(setups, setup)) {
          const [route, kit] = setups[setup];
          choose('mount_route', route);
          if (kit) choose('kit_choice', kit);
        }
      }
      this.querySelector('[data-zf-source-title]').textContent = title;
      const sourceSummary = this.querySelector('[data-zf-source]');
      sourceSummary.hidden = false;
      if (this.fromKnownDevice) {
        const change = document.createElement('button');
        change.type = 'button';
        change.className = 'zr-change-device';
        change.textContent = this.strings.edit;
        change.addEventListener('click', () => this.showStep(0));
        sourceSummary.append(change);
      }
    }
  });
}













